/* eslint-disable import/no-named-as-default */
import React, { useEffect, useRef, useState } from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import paginationUtils from 'angieslist-app-platform/lib/common/utils/paginationUtils';
import { Col, EllipsedPagination, Row, Alert } from 'angieslist-react-components';
import { analytics, getErrorMessage } from 'angieslist-app-platform';
import NotFound from 'angieslist-office-app-common/lib/v1/notFound/notFound';
import { useEnhancedPageView } from '@angi/snowplow-react';
import { SplitSdk } from '@splitsoftware/splitio-react';
import urlCatalog from '@angi/url-catalog';
import { AppraisersIcon } from '../modules/browser/home/common/Icons';
import { defaultPageProps } from '../../../app/browser/analyticsDefaultProperties';
import './BusinessListPage.scss';
import Options from '../modules/browser/home/BusinessList/components/Options';
import FlatList from '../modules/browser/home/BusinessList/components/FlatList';
import HelpFooter from '../modules/browser/home/BusinessList/components/HelpFooter';
import { itemClicked } from '../../../app/browser/analyticsHelpers';
import serviceProviderActions from '../../../app/common/serviceProvider/serviceProviderActions';
import CategorizedList from '../modules/browser/home/BusinessList/components/CategorizedList';
import BottomDrawerModal from '../modules/browser/home/BusinessList/components/BottomDrawerModal';
import { DEFAULT_OFFSET, PROS_PER_PAGE } from '../modules/browser/home/common/BusinessListConstants';
import businessListActions from '../../../app/common/businessList/businessListActions';
import { PRO_LEADS_PRODUCTINFO_FRONTEND_BUSINESS_SELECT, splitSdkConfig } from '../../common/splitSdkConfigSetup';
import resolveSelectedProDestination from '../common/selectedProDestination';

const sortTypes = {
  ratings: 'Customer ratings (high to low)',
  reviews: 'Total reviews (high to low)',
};

export const BusinessListPage = ({
  businessList,
  serviceProviderCache,
  getServiceProviders,
  isAuthenticated,
  config,
}) => {
  const [showProductTypeSplitFlag, setShowProductTypeSplitFlag] = useState(false);
  const [splitFlagChecked, setSplitFlagChecked] = useState(false);


  useEffect(() => {
    const client = SplitSdk(splitSdkConfig(config.splitClientSideApiKey)).client();
    client.on(client.Event.SDK_READY, () => {
      const treatment =
        client.getTreatment(PRO_LEADS_PRODUCTINFO_FRONTEND_BUSINESS_SELECT);
      setShowProductTypeSplitFlag(treatment === 'on');
      setSplitFlagChecked(true);
    });
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && splitFlagChecked) {
      getServiceProviders({
        limit: PROS_PER_PAGE, offset: DEFAULT_OFFSET,
      });
    }
  }, [splitFlagChecked]);

  const snowplowParams = {
    pageName: 'sp : business list',
  };

  useEnhancedPageView(snowplowParams);
  const [sortByValue, setSortByValue] = useState('');
  const [openFilter, setOpenFilter] = useState(false);
  const [isResolvingDestination, setIsResolvingDestination] = useState(false);
  const isResolvingDestinationRef = useRef(false);

  useEffect(() => {
    const onPageShow = () => {
      isResolvingDestinationRef.current = false;
      setIsResolvingDestination(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const toggleBottomFilter = (openFilterValue) => {
    setOpenFilter(openFilterValue);
  };

  const addActivityCount = (sp) => {
    if (sp.proType.toLowerCase() === 'leads') {
      const { unreadLeadCount = 0, opportunityCount = 0 } = serviceProviderCache[`HA-${sp.id}`] || {};
      return { ...sp, activityCount: unreadLeadCount + opportunityCount };
    }
    const {
      unreadLeadCount = 0,
      unreadDealCount = 0,
      unreadQuoteCount = 0,
      unreadReviewCount = 0,
      unreadMessageCount = 0,
    } = serviceProviderCache[sp.id] || {};
    const total = (
      unreadLeadCount
      + unreadDealCount
      + unreadQuoteCount
      + unreadReviewCount
      + unreadMessageCount
    );
    return {
      ...sp,
      activityCount: total,
    };
  };
  const {
    sortedBy = '',
    serviceProviders = [],
    totalCount = 0,
    currentOffset = 0,
  } = get(businessList, 'getServiceProviders.response') || {};

  const isLoading = get(businessList, 'getServiceProviders.isLoading', false);

  // businessListReducer redirect users with one business to homepage for Ads/Leads business.
  const showLoadingSpinner = totalCount === 1 || isLoading || !isAuthenticated;

  const error = get(businessList, 'getServiceProviders.error');
  const hasNoBusinesses = totalCount === 0;

  const selectedSortBy = sortedBy.toLowerCase();

  const onCardClicked = (id, proType) => {
    if (isResolvingDestinationRef.current) return;
    isResolvingDestinationRef.current = true;
    setIsResolvingDestination(true);
    resolveSelectedProDestination(id, proType)
      .then((destination) => {
        window.location.assign(destination);
      })
      .catch(() => {
        if ((proType || '').toLowerCase() === 'leads') {
          window.location.assign(urlCatalog.office.leads.homePage(id));
          return;
        }
        window.location.assign(`/app/home/${id}`);
      });
  };

  const spsWithNotificationCounts = serviceProviders.map(addActivityCount);
  const pageIndex = paginationUtils.getPageNum(currentOffset, PROS_PER_PAGE) - 1;
  const pageCount = paginationUtils.getPageCount(totalCount, PROS_PER_PAGE);
  const list = selectedSortBy === 'name' ?
    <CategorizedList serviceProviders={spsWithNotificationCounts} sortField="name" onCardClicked={onCardClicked} productInfoFeatureFlag={showProductTypeSplitFlag} /> :
    (<FlatList
      serviceProviders={spsWithNotificationCounts}
      onCardClicked={onCardClicked}
      productInfoFeatureFlag={showProductTypeSplitFlag}
    />);

  const optionsProps = {
    setSortBy: setSortByValue,
    sortByValue,
  };

  return (
    <div className="businesspage-container">
      <Col lg={3} md={4} sm={5} xsHidden>
        {!openFilter ? <Options {...optionsProps} /> :
        <BottomDrawerModal
          open={openFilter}
          closeDrawer={toggleBottomFilter}
          optionProps={optionsProps}
        />}
      </Col>
      <Choose>
        <When condition={showLoadingSpinner}>
          <div style={{ position: 'relative', height: '100%', minHeight: 550 }}>
            <div className="text-center">
              <i className="icon icon-load text-primary icon-pulse icon-5x text-center" />
            </div>
          </div>
        </When>
        <When condition={error}>
          <Alert bsStyle="danger">
            {getErrorMessage(error)}
          </Alert>
        </When>
        <Otherwise>
          <Col className="businesslist-data-container" xs={12} md={8} sm={12} lg={9}>
            <div className="businesslist-scroll-container">
              <div className="scroll-body">
                <div className="business-title-container">
                  <div className="h2 businesslist-title">
                    <b>Choose a business to manage</b>
                    {selectedSortBy !== 'name' && <div className="h4 business-sort-type">{sortTypes[selectedSortBy]}</div>}
                  </div>
                </div>
                <Choose >
                  <When condition={hasNoBusinesses}>
                    <NotFound
                      title="You don’t have any businesses yet."
                      subtitle="When you do, you can find them here."
                      buttonText="Refresh"
                      onClick={() => window.location.reload()}
                      className="empty-business-list-container"
                      useLegacyWrapper={false}
                      customIcon={<AppraisersIcon />}
                    />
                  </When>
                  <Otherwise>
                    <div className="filter-wrapper">
                      <Col lgHidden mdHidden smHidden>
                        <div id="options-filter-btn" role="button" tabIndex={0} className="options-filter-btn" onClick={() => toggleBottomFilter(true)} onKeyDown={() => toggleBottomFilter(true)}>
                        Sort <i className="icon icon-down-open" />
                        </div>
                      </Col>
                    </div>
                    <div
                      style={isResolvingDestination ? { pointerEvents: 'none' } : undefined}
                      aria-busy={isResolvingDestination || undefined}
                    >
                      {list}
                    </div>
                  </Otherwise>
                </Choose>
              </div>
              <If condition={pageCount > 1}>
                <Row>
                  <Col xs={12} md={8}>
                    <EllipsedPagination
                      pageCount={pageCount}
                      pageIndex={pageIndex}
                      onChange={(index) => {
                        const newOffset = paginationUtils.getOffset(index + 1, PROS_PER_PAGE);
                        getServiceProviders({
                          offset: newOffset,
                          limit: PROS_PER_PAGE,
                          sortBy: sortByValue,
                        });
                      }}
                    />
                  </Col>
                </Row>
              </If>
              <HelpFooter />
            </div>
          </Col>
        </Otherwise>
      </Choose>
    </div>
  );
};

const businessListPageAnalytics = {
  page: {
    name: 'SP : Business List',
    properties: defaultPageProps,
  },
  elements: {
    'sp-business-card': {
      click: {
        name: 'Item Clicked',
        properties: {
          'field-name': 'Bussiness Card Clicked',
        },
      },
      mouseover: {
        name: 'Item Clicked',
        properties: {
          activityLocation: 'SP : Business List',
          description: 'User hovers over the choose button on a business in the business list',
          spId: '<<attributes.spid.value>>',
        },
      },
    },
    email: itemClicked('SP : Business List', 'User clicks email support address'),
    phone: itemClicked('SP : Business List', 'User clicks phone support number'),
  },
};

BusinessListPage.propTypes = {
  businessList: PropTypes.object.isRequired,
  serviceProviderCache: PropTypes.object.isRequired,
  getServiceProviders: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  config: PropTypes.object.isRequired,
};

export default analytics({
  ruleset: businessListPageAnalytics,
})(connect(
  state => ({
    businessList: state.businessList,
    serviceProviderCache: state.serviceProviderCache,
    experiment: state.experimentV2,
    isAuthenticated: !!state.auth.isAuthenticated,
  }),
  {
    ...businessListActions,
    ...serviceProviderActions,
  },
)(React.memo(BusinessListPage)));



// WEBPACK FOOTER //
// ./src/modules/home/browser/BusinessListPage.js